import { FC, useEffect } from 'react';
import { useLocation } from 'react-router';

import { useGetNotificationsToolbarContext } from 'app/modules/notifications/components/_context';
import { useReadNotificationsMutation } from 'app/redux/api/notification/notification.api';
import { NotificationLocationState } from 'app/types/notifications.interface';

interface NotificationReadIndicatorCellProps {
    id: number
    readed: boolean
    successCallback?: () => void
}

export const NotificationReadIndicator: FC<NotificationReadIndicatorCellProps> = ({ id, readed, successCallback }) => {
  const locationState = useLocation().state as NotificationLocationState;

  const [requestReadNotifications, { error, reset }] = useReadNotificationsMutation();

  const { onReadItem } = useGetNotificationsToolbarContext();

  useEffect(() => {
    if (error) {
      const timeout = setTimeout(() => reset(), 3000);
      return () => clearTimeout(timeout);
    }
  }, [error, reset]);

  useEffect(() => {
    if (locationState?.id === id && !readed) {
      handleReadNotification();
    }
  }, [locationState, id, readed]);

  const handleReadNotification = () => {
    // Don't try to mark as read if it's already read
    if (readed) return;

    requestReadNotifications([id])
      .unwrap()
      .then(() => {
        if (successCallback) {
          successCallback();
        }

        // Cache invalidation from the mutation will handle updates automatically
        // Only call onReadItem if we're in the notifications context
        if (onReadItem) {
          onReadItem(id);
        }
      })
      .catch(() => {
      });
  };

  if (readed) {
    return null;
  }

  return (
    <span
      className={`bullet bullet-dot bg-${!error ? 'warning' : 'danger animation-blink'} h-8px w-8px cursor-pointer`}
      onClick={handleReadNotification}
    />
  );
};